import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import { useParams } from 'react-router-dom';

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import ProductClass from '../../../types/ProductClass';
import RequestState from '../../../types/RequestState';
import { calculateTotal, roundWithPrecision } from '../../../util/number';
import ScrollableAnchor from '../../anchor/ScrollableAnchor';
import Icon from '../../common/Icon';
import ProductCollectionMatrix from '../ProductCollectionMatrix';
import ProductMultiMatrix from '../ProductMultiMatrix';
import { PRODUCT_INFORMATION_BANNER_ID } from '../ProductPageContent';
import ProductSizeGuides from '../ProductSizeGuides';
import {
  updateProductSelection,
  createAnalyticsProducts,
} from '../utils/matrixSelections';

const ProductMatrix = ({
  analytics,
  accountStore,
  cartStore,
  configStore,
  product,
}) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [analyticsProducts, setAnalyticsProducts] = useState([]);
  const [matrixClearKey, setMatrixClearKey] = useState(0);
  const prevMatrixSelectionRef = useRef(null);
  const { id } = useParams();

  const sendAnalyticsEvents = useCallback(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    const analyticsProduct = {
      product,
    };

    analytics.productDetail({
      productList: [analyticsProduct],
      value: product.getPriceWithPrecision(
        accountStore.showPricesWithTax,
        product.id
      ),
    });
  }, [id]);

  // Initial analytics on mount
  useEffect(() => {
    configStore.analytics.ga4.enabled && sendAnalyticsEvents();
  }, [id]);

  // Handle product ID changes
  useEffect(() => {
    return () => {
      // Clear old product selection from store on unmount or product change
      cartStore.clearMatrixSelection(product.id);
      cartStore.updateProductQuantity(0);
    };
  }, [id]);

  // Reset state when product ID changes
  useEffect(() => {
    setSelectedProducts([]);
    setAnalyticsProducts([]);
    prevMatrixSelectionRef.current = null;
  }, [id]);

  const matrixSelection = cartStore.getMatrixSelection(product.id);
  const clearedByAddToCartProductId =
    cartStore.matrixClearedFromAddToCartProductId;

  useEffect(() => {
    const hadProducts = prevMatrixSelectionRef.current?.products?.length > 0;
    const nowEmpty =
      !matrixSelection?.products || matrixSelection.products.length === 0;
    const clearedByAddToCart =
      String(clearedByAddToCartProductId) === String(product.id);
    if (hadProducts && nowEmpty && clearedByAddToCart) {
      setSelectedProducts([]);
      setAnalyticsProducts([]);
      setMatrixClearKey((k) => k + 1);
      cartStore.clearMatrixClearFromAddToCartFlag();
    }
    prevMatrixSelectionRef.current = matrixSelection;
  }, [matrixSelection, clearedByAddToCartProductId, product.id]);

  const calculateTotalAnalyticsValue = useCallback(() => {
    const precision = 4;
    return roundWithPrecision(
      calculateTotal(
        analyticsProducts.map((product) => product.price * product.quantity),
        precision
      ),
      precision
    );
  }, [analyticsProducts]);

  const populateCart = useCallback(() => {
    const productsValid = selectedProducts && selectedProducts.length > 0;
    const analyticsValid = analyticsProducts && analyticsProducts.length > 0;

    if (!productsValid) return;

    const productsAdded = selectedProducts;
    const analyticsAdded = analyticsProducts;

    cartStore.clearMatrixSelection(product.id, true);
    cartStore.updateProductQuantity(0);
    setSelectedProducts([]);
    setAnalyticsProducts([]);
    setMatrixClearKey((k) => k + 1);

    cartStore
      .populate(productsAdded, accountStore.showMultiProductMatrix)
      .then(() => {
        if (analyticsValid) {
          const totalValue = roundWithPrecision(
            calculateTotal(
              analyticsAdded.map((p) => p.price * p.quantity),
              4
            ),
            4
          );
          analytics.addToCart(analyticsAdded, totalValue);
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        }
      });
  }, [selectedProducts, analyticsProducts]);

  const handleProductQuantity = useCallback(
    (newProduct) => {
      const withTax = accountStore.showPricesWithTax;

      // Use shared utility to update selection
      const newSelectedProducts = updateProductSelection(
        selectedProducts,
        newProduct.id,
        newProduct.qty
      );

      // Create analytics products
      const newAnalyticsProducts = createAnalyticsProducts(
        newSelectedProducts,
        product,
        withTax
      );

      // Calculate total quantity for navigation row
      const totalQuantity = newSelectedProducts.reduce(
        (sum, p) => sum + p.qty,
        0
      );

      // Update local state
      setSelectedProducts(newSelectedProducts);
      setAnalyticsProducts(newAnalyticsProducts);

      // Persist to CartStore
      cartStore.setMatrixSelection(
        product.id,
        newSelectedProducts,
        newAnalyticsProducts
      );

      // Sync product quantity for navigation row
      cartStore.updateProductQuantity(totalQuantity);
    },
    [selectedProducts]
  );

  const renderAddToCartButton = (classname) => {
    const loading = cartStore.state === RequestState.LOADING;

    return (
      <Button
        className={classname}
        onClick={populateCart}
        color="primary"
        disabled={loading}
        block
      >
        {loading ? (
          <Icon name="circle-o-notch" spin={loading} />
        ) : (
          <FormattedMessage
            id="productCollectionMatrix.addSelectedProducts"
            defaultMessage="Add Selected Products"
          />
        )}
      </Button>
    );
  };

  const renderButtonElement = () => {
    const showSizeGuide =
      !accountStore.isViewOnly && product.sizeGuides.length > 0;

    return (
      <div className="ProductMatrix__top_buttons">
        {renderAddToCartButton('ProductMatrix__add-to-cart-top')}
        {showSizeGuide && <ProductSizeGuides sizeGuides={product.sizeGuides} />}
      </div>
    );
  };

  const renderImageCarouselHelpText = () => {
    const count = product.multi.getMultiElementProperties()?.length ?? 0;
    if (count === 1) {
      return (
        <div className="ProductMatrix__info-help-image">
          <FormattedMessage
            id="matrix.imageHelp"
            defaultMessage="Images can be inspected on fullscreen by clicking on them."
          />
        </div>
      );
    }
  };

  const renderNoProductHelpText = () => {
    const count = product.multi.getMultiElementProperties()?.length ?? 0;
    if (count >= 2) {
      return (
        <div className="ProductMatrix__info-help-no">
          <Icon name="ban" />
          {' = '}
          <FormattedMessage
            id="matrix.noProductHelp"
            defaultMessage="Variation does not exist."
          />
        </div>
      );
    }

    return null;
  };

  const renderMultiMatrixInfo = () => {
    return (
      <div className="ProductMatrix__info">
        <FormattedMessage {...globalTranslations.productMatrixInfo} />
        {renderNoProductHelpText()}
        {renderImageCarouselHelpText()}
      </div>
    );
  };

  const renderAnchor = () => (
    <ScrollableAnchor
      id={PRODUCT_INFORMATION_BANNER_ID}
      scrollIfInHash={false}
    />
  );

  const renderCollectionMatrix = () => {
    const collection = product.collection;

    if (
      !collection ||
      (!collection.column && !collection.row) ||
      (!collection.column.elements && !collection.row.elements)
    ) {
      return null;
    }

    return (
      <>
        {renderAnchor()}
        <ProductCollectionMatrix
          product={product}
          productMatrix={product.collection}
        />
      </>
    );
  };

  const renderMultiMatrix = () => {
    return (
      <>
        {renderAnchor()}
        {renderMultiMatrixInfo()}
        {renderButtonElement()}
        <ProductMultiMatrix
          key={matrixClearKey}
          onHandleProductQuantity={handleProductQuantity}
          multi={product.multi}
        />
        {renderAddToCartButton('ProductMatrix__add-to-cart-bottom')}
      </>
    );
  };

  if (product.class === ProductClass.COLLECTION) {
    return renderCollectionMatrix();
  } else if (product.class === ProductClass.MULTI) {
    return renderMultiMatrix();
  }
  return null;
};

ProductMatrix.propTypes = {
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  product: modelOf(FullProduct).isRequired,
};

export default inject(
  'analytics',
  'accountStore',
  'cartStore',
  'configStore'
)(observer(ProductMatrix));
